import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Button } from '@clientbase/clientbase-library'
import { Stack, useMediaQuery } from '@mui/material'
import ModalNegativation from 'templates/v2/Negativation/components/FormNegativation'

import Can from 'components/Can'
import CreateGate from 'components/CreateGate'
import { Tag } from 'components/TagsAddInput/TagsAddInput'
import TagsPopover from 'components/TagsPopover'

import { convertTagsStringToArray } from 'utils/tags'

import { theme } from 'styles/theme'

import { ModalChargeDetailContext } from '../../context'
import { getStatus } from '../../ModalChargeDetail.utils'

interface TagForm {
  tags: Tag[]
}

const Actions: React.FC = () => {
  const {
    invoice,
    updateModal,
    updateReload,
    reload,
    updateRealtimeTags,
    optimisticTagUpdate
  } = useContext(ModalChargeDetailContext)
  // Estado local: o modal de detalhe abre de ~30 lugares e só 3 têm o
  // ChargeContext por perto — fora deles o toggleModal do contexto era undefined
  // e o botão "Negativar" não fazia nada. Mesmo motivo do "Duplicar".
  const [isOpenNegativation, setIsOpenNegativation] = useState(false)
  const isMobile = useMediaQuery('(max-width:600px)')

  // Convert invoice tags to Tag[] format for the form
  const currentTags: Tag[] = convertTagsStringToArray(invoice?.tags || '', [])

  // Form for TagsPopover
  const { control, setValue } = useForm<TagForm>({
    defaultValues: {
      tags: currentTags
    }
  })

  // Update form when invoice tags change
  useEffect(() => {
    if (invoice?.tags) {
      const tags = convertTagsStringToArray(invoice.tags, [])
      setValue('tags', tags)
    }
  }, [invoice?.tags, setValue])

  const handleOpenEditCharge = () => {
    updateModal((prevState) => ({ ...prevState, isOpenEditCharge: true }))
  }

  function handleOpenDialogCancel(uuid: string) {
    updateModal((prevState) => ({
      ...prevState,
      isOpenConfirmCancelModal: true,
      uuid
    }))
  }

  function handleOpenDialogAntecipate(uuid: string) {
    updateModal((prevState) => ({
      ...prevState,
      isOpenDialogAntecipate: true,
      uuid
    }))
  }

  function handleOpenMarkAsPaid() {
    updateModal((prevState) => ({
      ...prevState,
      isOpenModalPaid: true
    }))
  }

  const isPaid = getStatus(invoice?.status)?.label === 'Paga'
  // Cobrança de previsão (recebimento manual): não há emissão a adiantar, nada é
  // emitido em adquirente nenhuma e a baixa é manual. Ver SC-10679.
  //
  // Espelha o Billing#forecast_only? do backend, que compara o array e por isso
  // tolera ponto e vírgula sobrando (String#split descarta campos vazios no fim).
  // Em produção existem 315 cobranças gravadas como "direct_payment;", que uma
  // comparação exata deixaria passar.
  const isForecastOnly =
    invoice?.paymentType?.split(';').filter(Boolean).join(';') ===
    'direct_payment'

  if (isPaid) {
    return (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        gap={1}
        width={'100%'}
        justifyContent="flex-end"
        alignItems={isMobile ? 'stretch' : 'center'}
      >
        <TagsPopover
          control={control}
          name="tags"
          saveMode="immediate"
          saveContext={{
            taggableType: 'Billing',
            taggableUuid: invoice?.uuid || ''
          }}
          onSave={async (tags: Tag[]) => {
            updateRealtimeTags(tags)
            toast.success('Tags atualizadas com sucesso!')
            updateReload(!reload)
          }}
          onOptimisticUpdate={(tags: Tag[], isOptimistic: boolean) => {
            optimisticTagUpdate(tags, isOptimistic)
          }}
          maxTags={10}
        />
      </Stack>
    )
  }

  return (
    <>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        gap={1}
        width={'100%'}
        justifyContent="flex-end"
        alignItems={isMobile ? 'stretch' : 'center'}
      >
        {/* TagsPopover positioned to the right */}
        <Stack direction="row" alignItems="center">
          <TagsPopover
            control={control}
            name="tags"
            saveMode="immediate"
            saveContext={{
              taggableType: 'Billing',
              taggableUuid: invoice?.uuid || ''
            }}
            onSave={async (tags: Tag[]) => {
              // Update real-time tags immediately for instant UI feedback
              updateRealtimeTags(tags)
              // Show success notification
              toast.success('Tags atualizadas com sucesso!')
              // Reload the charge to refresh data
              updateReload(!reload)
            }}
            onOptimisticUpdate={(tags: Tag[], isOptimistic: boolean) => {
              // Update real-time tags optimistically for immediate feedback
              optimisticTagUpdate(tags, isOptimistic)
            }}
            maxTags={10}
          />
        </Stack>

        {/* Action buttons container */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          gap={1}
          justifyContent="end"
        >
          <Can permission="billings:manage">
            <>
              {(['no_payment', 'expiring'].includes(invoice?.status) ||
                (invoice?.status === 'open_payment' &&
                  invoice?.dueDate &&
                  new Date(invoice.dueDate) < new Date())) && (
                <Can permission="negativation:manage">
                  <CreateGate resourceName="Negativação">
                    <Button
                      sx={{
                        [theme.breakpoints.down(450)]: {
                          width: '100%'
                        }
                      }}
                      onClick={() => setIsOpenNegativation(true)}
                      fullWidth={isMobile}
                    >
                      Negativar
                    </Button>
                  </CreateGate>
                </Can>
              )}

              <Button
                sx={{
                  [theme.breakpoints.down(450)]: {
                    width: '100%'
                  }
                }}
                onClick={handleOpenEditCharge}
                variantButton="neutral"
                fullWidth={isMobile}
              >
                Editar fatura
              </Button>

              {getStatus(invoice?.status)?.label !== 'Cancelada' && (
                <Can permission="billings:cancel">
                  <Button
                    sx={{
                      [theme.breakpoints.down(450)]: {
                        width: '100%'
                      }
                    }}
                    onClick={() => handleOpenDialogCancel(invoice?.uuid)}
                    variantButton="neutral"
                    fullWidth={isMobile}
                  >
                    Cancelar fatura
                  </Button>
                </Can>
              )}

              {getStatus(invoice?.status)?.label === 'Pendente' &&
                !isForecastOnly && (
                  <Button
                    sx={{
                      [theme.breakpoints.down(450)]: {
                        width: '100%'
                      }
                    }}
                    onClick={() => handleOpenDialogAntecipate(invoice?.uuid)}
                    variantButton="pricing"
                    fullWidth={isMobile}
                  >
                    Adiantar emissão
                  </Button>
                )}
            </>
          </Can>

          <Can permission="billings:mark_as_paid">
            <Button
              sx={{
                [theme.breakpoints.down(450)]: {
                  width: '100%'
                }
              }}
              variantButton="primary"
              onClick={handleOpenMarkAsPaid}
              fullWidth={isMobile}
            >
              Marcar como paga
            </Button>
          </Can>
        </Stack>

        <ModalNegativation
          toggleModal={() => setIsOpenNegativation((prev) => !prev)}
          modalNegativation={isOpenNegativation}
          invoice={invoice}
        />
      </Stack>
    </>
  )
}

export default Actions
